// Street Interview Ad's form (live's `street-interview-ad-form/library-view`, spec
// G14-street-interview-ad, captures 131 to 146, 172 to 174 and 225 to 238): live's staged form in
// three approvals. The script stage picks the product (one of the brand's, or an uploaded
// packshot), the cast (Auto, or the interviewer and up to four people), writes the script (the
// server's pack reader and director, or the member) and approves it; the cast stage shows the
// street and the five people; Approve cast draws the keyframes; Generate final video starts the
// run. The draft is the server's (`street-draft.ts`).
import { Group, Loader, Stack, Text, Tooltip, UnstyledButton } from "@mantine/core";
import { Smartphone, Type, Users } from "lucide-react";
import { useState, type ReactNode } from "react";

import { useGetCatalog } from "../../../api/generated/endpoints/generation/generation";
import type { ActorSource, StreetScript } from "../../../api/generated/model";
import {
  VideoTemplateQuote,
  VideoTemplateRequest,
  streetInterviewAdInputsCtaMax,
  streetInterviewAdInputsPronunciationMax,
  streetInterviewAdInputsQuestionMax,
  type StreetInterviewAdInputsOutput,
} from "../../../api/generated/zod";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { usePricedStart } from "../../../platform/jobs/priced-start";
import { ActorsComposer } from "../../../platform/actors/composer/ActorsComposer";
import { actorModels } from "../../../platform/actors/composer/GenerateWithText";
import { pickedOfActor, type PickedActor } from "../../../platform/actors/picked";
import { useMakings } from "../../../platform/actors/use-makings";
import { Choice } from "../../../ui/Choice";
import { GlassPanel } from "../../../ui/GlassPanel";
import { ReuseSwitch, type TemplateFormProps } from "./kit";
import { CastTile, sourceKey, useProductPick, type PickedPictures } from "./PodcastTiles";
import { InputChip, ScriptSection } from "./PodcastScript";
import { Submit, Subtitles } from "./staged";
import { StreetCastStage } from "./StreetCast";
import {
  AutoCastTile,
  INTERVIEWER_TOOLTIP,
  LetAiChoose,
  PeopleTile,
  StreetProductTile,
  type Seat,
} from "./StreetTiles";
import {
  CAST_STILLS,
  KEYFRAME_STILLS,
  emptyForm,
  exampleForm,
  stageStills,
  useStreetDraft,
  videoBody,
  type DraftWork,
  type VideoSettings,
} from "./street-draft";
import {
  ACTOR_PENDING,
  CAST_PENDING,
  GENERATE_SCRIPT,
  PICK_PRODUCT,
  SCRIPT_PROBLEMS,
  STILLS_PENDING,
  UNREADABLE_PACK,
  blankScript,
  maxWordsAt,
  sameLineTexts,
  scriptIssue,
  scriptMeta,
} from "./street-script";
import classes from "./podcast.module.css";

/** Live's words (`library-view/constants.ts`). */
const SCRIPT_TOOLTIP =
  "17 fixed lines: the question round, the hand-over and the reactions. Edit any line before " +
  "approving — every product fact is quoted from the product text.";
const CROWD_TOOLTIP =
  "Who gets stopped on the street. Auto picks it from your product's main buyer; a picked cast " +
  "photo always wins for that person.";
const TITLE_TOOLTIP =
  'A title line over the first 20 seconds, e.g. "Asked people downtown what energy drink they ' +
  'reach for". Off by default.';
const FORMAT_TOOLTIP = "Every image and clip is rendered vertical 9:16 at 1080p.";
const WRITE_SCRIPT = "Write the 17 lines yourself";
const SCRIPT_HINT = {
  wand: "to write the interview from the product, or",
  pencil: "to write the 17 lines yourself.",
};
const APPROVE_SCRIPT = "Approve script";
const APPROVE_CAST = "Approve cast";
const GENERATE_VIDEO = "Generate final video";
const HANDOFF_NOTICE =
  "Your street interview ad is finishing in My Generations — you can close this panel now.";
const CROWDS = [
  { value: "auto", label: "Auto" },
  { value: "men", label: "Men" },
  { value: "women", label: "Women" },
  { value: "mixed", label: "Mixed" },
] as const;
const CHIPS = [
  {
    field: "question",
    label: "Question",
    placeholder: "What do you use on roaches at home? (optional)",
    tooltip:
      'The everyday question the interviewer asks everyone, e.g. "What do you use on roaches at ' +
      'home?". Leave empty and the director writes it.',
    maxLength: streetInterviewAdInputsQuestionMax,
  },
  {
    field: "cta",
    label: "CTA",
    placeholder: "Shop now (optional)",
    tooltip: "The button label on the end card. Leave empty and the director writes a short one.",
    maxLength: streetInterviewAdInputsCtaMax,
  },
  {
    field: "pronunciation",
    label: "Pronunciation",
    placeholder: "SKWOCH (optional)",
    tooltip:
      "How your brand name is said, e.g. SKWOCH. Leave empty and the director decides what needs " +
      "a respelling.",
    maxLength: streetInterviewAdInputsPronunciationMax,
  },
] as const;

/** A person of the cast: the interviewer or a seat. */
type Person = "interviewer" | Seat;
interface PersonMaking {
  jobId: string;
  failed: boolean;
  person: Person;
}

/** Live's progress line under the form (`run-progress.ts`). */
function progressOf(work: DraftWork, keyframes: boolean, drawn: number): string | null {
  if (work.reading) return "Reading the pack";
  if (work.writing) return "Writing the script";
  if (work.planning) return "Planning the shots";
  if (!work.drawing) return null;
  return keyframes
    ? `Generating keyframes · ${drawn}/${KEYFRAME_STILLS.length}`
    : `Generating the cast · ${drawn}/${CAST_STILLS.length}`;
}

/**
 * Live's form: "Reuse example" (on writes the card's example to the draft, its script approved as
 * it is; off, the empty form; locked once the script is approved or while anything works;
 * Replicate opens with it on, written with the first edit or ask, G14 Street Interview-13, and
 * off before then shows the saved draft again, G14-street-reuse-notes), the
 * stage, and in the footer the crowd, the subtitles, the title, the 9:16 format and the round
 * start: "Approve script", "Approve cast", then "Generate final video", whose tooltip says what
 * it waits for. Subtitles and the title stay editable after the approvals, as live's; Generate
 * final video sends them.
 */
export function StreetInterviewAdForm(props: TemplateFormProps) {
  const { teamId, brandId, above, example, onStarted } = props;
  const [collapsed, setCollapsed] = useState(false);
  const flow = useStreetDraft(teamId, brandId);
  const catalog = useGetCatalog().data;
  const [composing, setComposing] = useState<Person | null>(null);
  const [makings, setMakings] = useState<PersonMaking[]>([]);
  const [pictures, setPictures] = useState<PickedPictures>({});
  const [baseline, setBaseline] = useState<StreetScript | null | undefined>(undefined);
  const [submitted, setSubmitted] = useState(false);
  const [wasWriting, setWasWriting] = useState(false);
  const [productAsked, setProductAsked] = useState(false);
  // The video's settings once the script is approved (the draft takes no change then); dropped
  // when the draft is back at its script (Start over), so a later approval starts from the form.
  const [settings, setSettings] = useState<VideoSettings | null>(null);
  // Live's "Reuse example" switch: as last set, else on while the draft holds the example's pick.
  const [reuse, setReuse] = useState<boolean | null>(null);
  const [replicated, setReplicated] = useState(false);
  const reusable = exampleForm(example?.inputs);
  const { form, draft } = flow;
  const picked = form?.inputs.product;
  const catalogue = picked?.kind === "product" ? { product_id: picked.product_id, image_id: picked.image_id } : null;
  const product = useProductPick(teamId, brandId, catalogue);
  const writing = flow.work.reading || flow.work.writing;
  const approved = draft !== undefined && draft.stage !== "script";
  const shownSettings: VideoSettings | null =
    form === null
      ? null
      : (approved ? settings : null) ?? {
          subtitles: form.inputs.subtitles,
          caption_position: form.inputs.caption_position,
          caption_style_id: form.inputs.caption_style_id,
          title: form.inputs.title,
        };
  const video = draft === undefined || shownSettings === null ? null : videoBody(draft, shownSettings);
  const videoRequest = VideoTemplateRequest.safeParse(video);
  const videoQuote = VideoTemplateQuote.safeParse(video);
  const videoStart = usePricedStart({
    teamId,
    brandId,
    quote: videoQuote.success ? videoQuote.data : undefined,
    request: videoRequest.success ? videoRequest.data : undefined,
    onStarted,
  });
  if (!approved && settings !== null) setSettings(null);
  // What the form keeps beside the draft, afresh for a form showing `script`.
  const afresh = (script: StreetScript | null | undefined) => {
    setBaseline(script);
    setSubmitted(false);
    setProductAsked(false);
    setMakings([]);
    setPictures({});
  };
  // `shown` (Replicate's open): the form shows the example and the draft is written on the
  // member's first edit or ask, as live's mount values; the switch itself writes it. Off while
  // the example is only shown drops it unwritten, and the switch then shows what the saved
  // draft holds (G14-street-reuse-notes).
  const fill = (on: boolean, shown = false) => {
    if (!on && flow.held) {
      flow.discard();
      setReuse(null);
      afresh(draft?.script);
      return;
    }
    const next = on ? reusable : emptyForm();
    if (next === null) return;
    if (shown) flow.show(next);
    else flow.edit(next);
    setReuse(on);
    afresh(next.script);
  };
  if (draft !== undefined && baseline === undefined) setBaseline(draft.script);
  if (writing !== wasWriting) {
    setWasWriting(writing);
    if (!writing && draft !== undefined && draft.script_director !== "failed") setBaseline(draft.script);
  }
  // Replicate (live's `isExampleApplied`): the example shown on a draft still at its script,
  // written with the member's first edit or ask (live's `ensureRun`), never on the open alone.
  if (props.replicate && !replicated && draft !== undefined) {
    setReplicated(true);
    if (flow.editable && reusable !== null) fill(true, true);
  }

  const setInputs = (change: Partial<StreetInterviewAdInputsOutput>) => flow.editInputs(change);
  const cast = (person: Person, source: ActorSource | null) => {
    const current = form?.inputs.cast ?? {};
    setInputs({ cast: { ...current, [person]: source } });
  };
  const remember = (actor: PickedActor) =>
    setPictures((current) => ({ ...current, [sourceKey(actor.source)]: { url: actor.imageUrl, name: actor.name } }));
  const pick = (person: Person, actor: PickedActor) => {
    remember(actor);
    cast(person, actor.source);
  };
  useMakings({
    teamId,
    brandId,
    makings,
    onMade: (jobId, made) => {
      const making = makings.find((each) => each.jobId === jobId);
      setMakings((current) => current.filter((each) => each.jobId !== jobId));
      if (making !== undefined) pick(making.person, pickedOfActor(made));
    },
    onFailed: (jobId) =>
      setMakings((current) => current.map((each) => (each.jobId === jobId ? { ...each, failed: true } : each))),
  });

  const panel = (body: ReactNode, footer?: ReactNode) => (
    <GlassPanel
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      above={above}
      header={
        <ReuseSwitch
          example={reusable === null ? null : example}
          checked={reuse ?? form?.inputs.product?.kind === "example"}
          locked={!flow.editable}
          onChange={fill}
        />
      }
      summary={<Text size="sm">Street Interview Ad</Text>}
      footer={footer}
    >
      {body}
    </GlassPanel>
  );
  if (flow.read.isError) return panel(<ErrorAlert error={flow.read.error} />);
  if (draft === undefined || form === null || shownSettings === null) {
    return panel(<Loader size="sm" aria-label="Loading the draft" />);
  }

  const makers = catalog === undefined ? [] : actorModels(catalog.image_models);
  if (composing !== null && catalog !== undefined) {
    const person = composing;
    const close = () => setComposing(null);
    return (
      <ActorsComposer
        teamId={teamId}
        brandId={brandId}
        catalog={catalog}
        onStarted={(job) => {
          setMakings((current) => [
            ...current.filter((each) => each.person !== person),
            { jobId: job.id, failed: false, person },
          ]);
          close();
        }}
        place={{
          outputs: 1,
          uploadMost: 1,
          onCancel: close,
          onCreated: (made) => {
            const actor = made[0];
            if (actor !== undefined) pick(person, pickedOfActor(actor));
            close();
          },
        }}
      />
    );
  }

  const { inputs, script } = form;
  const locked = !flow.editable;
  const work = flow.work;
  const keyframes = draft.stage === "keyframes";
  const inStage = stageStills(draft);
  const drawn = draft.stills.filter((each) => inStage.includes(each.name) && each.status === "done").length;
  const progress = progressOf(work, keyframes, drawn);
  const issue = scriptIssue(script);
  const edited = script !== null && !sameLineTexts(script, baseline ?? null);
  const shownIssue = script !== null && !writing && (edited || submitted) ? issue : null;
  const makingFor = (person: Person) => makings.find((each) => each.person === person) ?? null;
  const changeSettings = (change: Partial<VideoSettings>) => {
    if (approved) setSettings({ ...shownSettings, ...change });
    else setInputs(change);
  };

  let submit;
  if (draft.stage === "cast") {
    const ready = CAST_STILLS.every(
      (name) => draft.stills.find((each) => each.name === name)?.status === "done",
    );
    submit = (
      <Submit
        label={APPROVE_CAST}
        reasons={ready ? [] : [CAST_PENDING]}
        disabled={!ready || flow.busy}
        loading={flow.approving}
        onClick={flow.approveCast}
      />
    );
  } else if (keyframes) {
    const ready = drawn === KEYFRAME_STILLS.length;
    const starting = videoStart.start.isPending;
    submit = (
      <Submit
        label={GENERATE_VIDEO}
        reasons={ready ? [] : [STILLS_PENDING]}
        disabled={!ready || flow.busy || !videoStart.canStart || starting}
        loading={starting}
        onClick={videoStart.startJob}
      />
    );
  } else {
    const reasons = [
      ...(inputs.product ? [] : [PICK_PRODUCT]),
      ...(script === null ? [GENERATE_SCRIPT] : []),
      // A failed AI Actor waits in its seat, shown failed, for its remove: it holds nothing up.
      ...(makings.some((each) => !each.failed) ? [ACTOR_PENDING] : []),
    ];
    submit = (
      <Submit
        label={APPROVE_SCRIPT}
        reasons={reasons}
        disabled={reasons.length > 0 || issue !== null || flow.busy}
        loading={flow.approving}
        onClick={() => {
          setSubmitted(true);
          flow.approve();
        }}
      />
    );
  }

  const footer = (
    <Group justify="space-between" wrap="nowrap" gap={12}>
      <div className={classes.settings}>
        <Choice
          setting="Crowd"
          icon={<Users size={16} strokeWidth={1.5} />}
          tip={CROWD_TOOLTIP}
          width={100}
          variant="footer"
          value={inputs.crowd}
          options={CROWDS.map((each) => ({ value: each.value, label: each.label }))}
          disabledBecause={locked ? CROWD_TOOLTIP : undefined}
          onChange={(next) => {
            const crowd = CROWDS.find((each) => each.value === next);
            if (crowd !== undefined) setInputs({ crowd: crowd.value });
          }}
        />
        <Subtitles
          on={shownSettings.subtitles}
          position={shownSettings.caption_position}
          disabled={flow.busy}
          onChange={(change) => changeSettings(change)}
        />
        <Tooltip label={TITLE_TOOLTIP} multiline maw={280} withArrow>
          <UnstyledButton
            className={classes.setting}
            data-on={shownSettings.title || undefined}
            aria-pressed={shownSettings.title}
            disabled={flow.busy}
            onClick={() => changeSettings({ title: !shownSettings.title })}
          >
            <Type size={16} strokeWidth={1.5} aria-hidden />
            {shownSettings.title ? "Title on" : "Title off"}
          </UnstyledButton>
        </Tooltip>
        <Tooltip label={FORMAT_TOOLTIP} multiline maw={280} withArrow>
          <span className={classes.setting}>
            <Smartphone size={16} strokeWidth={1.5} aria-hidden />
            9:16
          </span>
        </Tooltip>
      </div>
      {submit}
    </Group>
  );

  const refused = videoStart.start.error ?? videoStart.quoteError ?? flow.failure;
  const status = (
    <>
      {draft.problem === null ? null : (
        <Text className={classes.problem} role="alert">
          {draft.problem}
        </Text>
      )}
      {refused === null ? null : <ErrorAlert error={refused} />}
      {progress === null ? null : (
        <output className={classes.progress}>
          <Loader size={14} aria-hidden />
          {progress}
        </output>
      )}
      {videoStart.start.isSuccess && keyframes ? (
        <Text component="output" size="sm">
          Generation started. {HANDOFF_NOTICE}
        </Text>
      ) : null}
    </>
  );
  if (approved) {
    return panel(
      <div className={classes.content}>
        <StreetCastStage
          stage={draft.stage}
          stills={draft.stills}
          planning={work.planning}
          busy={flow.busy}
          resetting={flow.resetting}
          onRegenerate={flow.regenerate}
          onStartOver={flow.startOver}
        />
        {status}
      </div>,
      footer,
    );
  }

  const seats = {
    p1: inputs.cast.p1 ?? null,
    p2: inputs.cast.p2 ?? null,
    p3: inputs.cast.p3 ?? null,
    p4: inputs.cast.p4 ?? null,
  };
  const warnings = [
    ...(draft.pack_warning ? [UNREADABLE_PACK] : []),
    ...(draft.script_warning && script !== null ? [SCRIPT_PROBLEMS] : []),
  ];
  const body = (
    <div className={classes.content}>
      <Stack gap={12}>
        <div className={classes.tiles}>
          <StreetProductTile
            teamId={teamId}
            brandId={brandId}
            pick={inputs.product ?? null}
            typed={{ product_name: inputs.product_name, product_text: inputs.product_text }}
            disabled={locked}
            missing={productAsked}
            exampleUrls={example?.urls ?? {}}
            onChange={(product, typed) => setInputs({ product, ...typed, product_category: "" })}
          />
          {inputs.cast_mode === "auto" ? (
            <AutoCastTile disabled={locked} onCustomise={() => setInputs({ cast_mode: "choose" })} />
          ) : (
            <>
              <CastTile
                teamId={teamId}
                brandId={brandId}
                label="Interviewer"
                tooltip={INTERVIEWER_TOOLTIP}
                source={inputs.cast.interviewer ?? null}
                making={makingFor("interviewer")}
                pictures={pictures}
                disabled={locked}
                onMakeActor={makers.length === 0 ? null : () => setComposing("interviewer")}
                onPick={(actor) => pick("interviewer", actor)}
                onRemove={() => {
                  setMakings((current) => current.filter((each) => each.person !== "interviewer"));
                  cast("interviewer", null);
                }}
              />
              <PeopleTile
                teamId={teamId}
                brandId={brandId}
                seats={seats}
                making={makings.flatMap((each) =>
                  each.person === "interviewer" || each.failed ? [] : [each.person],
                )}
                failed={makings.flatMap((each) =>
                  each.person === "interviewer" || !each.failed ? [] : [each.person],
                )}
                pictures={pictures}
                disabled={locked}
                onMakeActor={makers.length === 0 ? null : (seat) => setComposing(seat)}
                onRetry={
                  makers.length === 0
                    ? null
                    : (seat) => {
                        setMakings((current) => current.filter((each) => each.person !== seat));
                        setComposing(seat);
                      }
                }
                onPick={(picks) => {
                  picks.forEach(({ actor }) => remember(actor));
                  const next = { ...inputs.cast };
                  picks.forEach(({ seat, actor }) => {
                    next[seat] = actor.source;
                  });
                  setInputs({ cast: next });
                }}
                onRemove={(seat) => {
                  setMakings((current) => current.filter((each) => each.person !== seat));
                  cast(seat, null);
                }}
              />
              <LetAiChoose disabled={locked} onChoose={() => setInputs({ cast_mode: "auto" })} />
            </>
          )}
        </div>
        <div className={classes.chips}>
          {CHIPS.map((chip) => (
            <InputChip
              key={chip.field}
              label={chip.label}
              value={inputs[chip.field]}
              placeholder={chip.placeholder}
              tooltip={chip.tooltip}
              maxLength={chip.maxLength}
              disabled={locked}
              onChange={(value) => {
                const change: Partial<StreetInterviewAdInputsOutput> = {};
                change[chip.field] = value;
                setInputs(change);
              }}
            />
          ))}
        </div>
        <ScriptSection
          lines={script?.lines ?? null}
          meta={script === null ? null : scriptMeta(script.lines)}
          tooltip={SCRIPT_TOOLTIP}
          primary="INTERVIEWER"
          maxWords={maxWordsAt}
          hint={SCRIPT_HINT}
          writeLabel={WRITE_SCRIPT}
          writing={writing}
          locked={locked}
          issue={shownIssue}
          warning={warnings.length === 0 ? null : warnings.join(" ")}
          onGenerate={() => {
            if (!inputs.product) {
              setProductAsked(true);
              return;
            }
            flow.generateScript();
          }}
          onWrite={() => {
            const blank = blankScript({
              question: inputs.question.trim(),
              crowd: inputs.crowd,
              productName: inputs.product?.kind === "upload" ? inputs.product_name.trim() : product.name,
              cta: inputs.cta.trim(),
            });
            setBaseline(blank);
            flow.edit({ script: blank });
          }}
          onLine={(index, text) => {
            if (script === null) return;
            flow.edit({
              script: { ...script, lines: script.lines.map((line, at) => (at === index ? { ...line, text } : line)) },
            });
          }}
        />
      </Stack>
      {status}
    </div>
  );
  return panel(body, footer);
}

